import type { ColumnVisibilityItem } from "providers/Auth";
import type {
  CodeToSendSlot,
  Lockbox,
  LockboxIntegratedVendor,
  LockboxVendor,
  LockboxVerificationStatus,
  VerifyReason
} from "api/landlord/lockboxes/types";

export const LOCKBOXES_TAB_KEY = "lockboxes";

// Persisted verification status -> display label + pill classes (design tokens).
export const VERIFICATION_STATUS_LABELS: Record<
  LockboxVerificationStatus,
  string
> = {
  not_verified: "Pending",
  verified: "Verified",
  failed: "Failed"
};

// Machine reason code -> short label shown in the drawer's "Reason" row (failed only).
export const VERIFY_REASON_LABELS: Record<VerifyReason, string> = {
  unrecognized_serial: "Serial number is not recognized",
  invalid_credentials: "Vendor credentials are invalid",
  no_integration: "No vendor account connected",
  vendor_error: "Couldn't complete the check with the vendor",
  not_applicable: "No vendor to verify against"
};

export const NO_ACCOUNT_ASSIGNED_LABEL = "No account assigned to this lockbox";

// Lowercase vendor value -> display label.
export const VENDOR_LABELS: Record<LockboxVendor, string> = {
  sentrilock: "SentriLock",
  codebox: "CodeBox",
  igloohome: "Igloohome",
  none: "No vendor"
};

export const VENDOR_OPTIONS: { value: LockboxVendor; label: string }[] = [
  { value: "sentrilock", label: VENDOR_LABELS.sentrilock },
  { value: "codebox", label: VENDOR_LABELS.codebox },
  { value: "igloohome", label: VENDOR_LABELS.igloohome },
  { value: "none", label: VENDOR_LABELS.none }
];

const NO_VENDOR: LockboxVendor = "none";

export const isNoVendor = (
  vendor: LockboxVendor | "" | null | undefined
): vendor is "none" => vendor === NO_VENDOR;

export const AUTO_GENERATED_CODE_LABEL = "Auto-generated per showing";

export const NOT_APPLICABLE_LABEL = "Not applicable";

export const CODE_TO_SEND_UNSET_LABEL = "Not set";
export const CODE_TO_SEND_UNSET_HINT =
  "Active is on but no code is chosen — self-guided showings on this unit have nothing to send.";

export const codeToSendSlotLabel = (slot: CodeToSendSlot) =>
  `Admin code ${slot}`;

export const lockboxCodeForSlot = (
  codes: Pick<Lockbox, "code" | "code_2" | "code_3">,
  slot: CodeToSendSlot
): string | null =>
  ({ 1: codes.code, 2: codes.code_2, 3: codes.code_3 })[slot]?.trim() || null;

export type AccountsByVendor<T> = Record<LockboxIntegratedVendor, T[]>;

// Column `id`s MUST byte-match the boompay-api Boom::Partner::User LOCKBOXES_DEFAULT_COLUMNS
// ids, or saved column preferences for the lockboxes tab are silently dropped on read (CRM-926).
export const DEFAULT_LOCKBOX_COLUMNS: ColumnVisibilityItem[] = [
  {
    id: "name",
    label: "Serial number",
    checked: true,
    disabled: false,
    always_on: true
  },
  { id: "code", label: "Admin code 1", checked: true, disabled: false },
  { id: "code_2", label: "Admin code 2", checked: true, disabled: false },
  { id: "code_3", label: "Admin code 3", checked: true, disabled: false },
  { id: "property", label: "Property", checked: true, disabled: false },
  { id: "unit", label: "Unit", checked: true, disabled: false },
  { id: "vendor", label: "Vendor", checked: true, disabled: false },
  { id: "vendor_account", label: "Account", checked: true, disabled: false },
  { id: "code_to_send", label: "Code to send", checked: true, disabled: false },
  { id: "notes", label: "Notes", checked: true, disabled: false },
  { id: "created_at", label: "Created at", checked: true, disabled: false },
  { id: "created_by", label: "Created by", checked: true, disabled: false },
  {
    id: "last_battery_replacement_date",
    label: "Last battery replacement",
    checked: true,
    disabled: false
  },
  { id: "active", label: "Active", checked: true, disabled: false },
  { id: "status", label: "Status", checked: true, disabled: false }
];
